import React from 'react'
import { createStore } from 'zustand'
import { ActionCardData, ActionCardModel, FlowData, QuestionData, SectionData } from '@playbooks/action-cards-utils/src'

import actionUpdate, { ActionUpdate } from './actions/actionUpdate'
import actionSetActionCards, { processActionCardsData, SetActionCards } from './actions/actionSetActionCards'
import { ActionCardsCallbacks, ActionCardsConfig, ActionCardsSettings, SlidesData } from '../types'
import { ActionCardsElement } from '../hooks/useActionCardsApiRegister'
import { Scope } from '../utils/makeSeparatorScopes'
import handleActionCards from '../utils/handleActionCards'

export type ActionCardsProps = ActionCardsCallbacks & {
  value: ActionCardData
  settings?: ActionCardsSettings
  // ActionCardsElement ref is needed to use API from the custom element
  ref?: React.RefObject<ActionCardsElement | null>
  config: ActionCardsConfig
}

type ActionCardsState = Omit<ActionCardsProps, 'settings' | 'ref' | 'config'> & {
  data: ActionCardData
  slides: SlidesData
  actionCardsModel: ActionCardModel
  questionsWithCode: QuestionData[]
  availableTags: Record<number, string>
  flowData: FlowData
  scopes: Scope[]
  visiblePinnedSections: SectionData[]
  highlightedIds: number[]

  isScrollButtonVisible: boolean
  setHighlightedIds: (ids: number[]) => void
  update: ActionUpdate
  setActionCards: SetActionCards
  getActualState: () => Store
  resetHighlights: (idsToReset: number[]) => void
  setScrollButtonVisible: (visible: boolean) => void
}

export type Store = Omit<ActionCardsState, 'value'>

export const createActionCardsStore = (
  initProps: ActionCardsProps,
  props: {
    prevActionCardsDataRef: React.RefObject<ActionCardData | null>
    nodesVisibilityRules: ActionCardsSettings['nodesVisibilityRules'] | undefined
  },
) => {
  const { availableTags, data, flowData, questionsWithCode } = processActionCardsData(initProps.value)

  handleActionCards(data)

  const actionCardsModel = new ActionCardModel(data || null!)

  // We need to get a new objet to prevent direct mutation
  props.prevActionCardsDataRef.current = JSON.parse(JSON.stringify(data)) as ActionCardData

  return createStore<Store>()(
    (set, get): Store => ({
      ...initProps,
      scopes: [],
      slides: {},
      data,
      actionCardsModel,
      availableTags,
      questionsWithCode,
      flowData,
      visiblePinnedSections: [],
      highlightedIds: [],
      isScrollButtonVisible: false,
      setActionCards: actionSetActionCards(set),
      update: actionUpdate(set, get, props),
      getActualState: get,
      setHighlightedIds: ids => set({ highlightedIds: ids }),
      resetHighlights: idsToReset => {
        const { highlightedIds } = get()
        const resetHighlights = highlightedIds.filter(id => {
          return !idsToReset.includes(id)
        })
        set({ highlightedIds: resetHighlights })
      },
      setScrollButtonVisible: visible => set({ isScrollButtonVisible: visible }),
    }),
  )
}

export type Get = () => Store
export type Set = (state: Store | ((state: Store) => Store)) => void
